import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "stocktools_cart_v3";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [coupon, setCoupon] = useState(() => {
    try { return localStorage.getItem("stocktools_coupon") || ""; } catch { return ""; }
  });
  const [pricing, setPricing] = useState(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => {
    if (coupon) localStorage.setItem("stocktools_coupon", coupon);
    else localStorage.removeItem("stocktools_coupon");
  }, [coupon]);

  const refresh = useCallback(async () => {
    if (items.length === 0) { setPricing(null); return; }
    try {
      const { data } = await api.post("/cart/preview", {
        items, coupon_code: coupon || null,
      });
      setPricing(data);
      // Auto-clear stale/invalid coupons so users don't get trapped
      if (coupon && data && data.coupon_code === null) {
        setCoupon("");
      }
    } catch { setPricing(null); }
  }, [items, coupon]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = (id) => {
    setItems((prev) => prev.find((i) => i.id === id) ? prev : [...prev, { type: "tool", id }]);
  };
  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const has = (id) => items.some((i) => i.id === id);
  const clear = () => { setItems([]); setCoupon(""); };
  const applyCoupon = (code) => setCoupon((code || "").toUpperCase().trim());

  return (
    <CartContext.Provider value={{ items, pricing, coupon, add, remove, has, clear, refresh, applyCoupon, count: items.length }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
